import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { GraduationCap, Award, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Academy() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [active, setActive] = useState(null);
  const load = () => api.get("/courses").then(r => setCourses(r.data));
  useEffect(() => { load(); }, []);
  const completed = courses.filter(c => c.progress?.completed).length;
  const percent = courses.length ? (completed / courses.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={user ? `/${user.role}` : "/"} className="text-slate-600 hover:text-brand flex items-center gap-1 text-sm" data-testid="academy-back"><ArrowLeft size={16} /> Back</Link>
            <Logo />
          </div>
          <Badge className="bg-[#FF6A00]/10 text-[#FF6A00] hover:bg-[#FF6A00]/10"><GraduationCap size={12} className="mr-1" /> Academy</Badge>
        </div>
      </header>
      <div className="max-w-7xl mx-auto p-6 lg:p-10 space-y-8">
        <div>
          <div className="eyebrow">SELLEXR Academy</div>
          <h1 className="mt-3 font-heading font-bold text-4xl">Train. Certify. Earn.</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">Free video courses on Indian e-commerce operations. Pass the quiz (60%+) to earn a shareable SELLEXR certificate.</p>
        </div>
        {user?.role === "freelancer" && (
          <div className="card-soft p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-bold">Your progress</div>
              <div className="text-sm text-slate-500">{completed} / {courses.length} courses</div>
            </div>
            <Progress value={percent} className="h-2 [&>div]:bg-[#FF6A00]" />
          </div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map(c => (
            <div key={c.id} className="card-soft overflow-hidden group" data-testid={`course-${c.id}`}>
              <div className="relative">
                <img src={`https://i.ytimg.com/vi/${c.youtube_id}/hqdefault.jpg`} alt={c.title} className="w-full h-44 object-cover" />
                {c.progress?.completed && (
                  <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full p-1"><CheckCircle2 size={16} /></div>
                )}
              </div>
              <div className="p-6">
                <div className="font-heading font-bold text-lg">{c.title}</div>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.description}</p>
                <div className="mt-3 flex items-center gap-2">
                  <Badge variant="secondary">{c.duration_min} min</Badge>
                  <Badge variant="secondary">{c.quiz?.length} questions</Badge>
                  {c.progress?.quiz_score != null && <Badge className="bg-[#FF6A00]/10 text-[#FF6A00] hover:bg-[#FF6A00]/10">Score {c.progress.quiz_score}%</Badge>}
                </div>
                <Button onClick={() => setActive(c)} className="mt-4 btn-orange hover:bg-[#FF6A00] w-full" data-testid={`course-open-${c.id}`}>
                  {c.progress?.completed ? "Review" : "Start course"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {active && <CourseModal course={active} onClose={() => { setActive(null); load(); }} />}
    </div>
  );
}

function CourseModal({ course, onClose }) {
  const [stage, setStage] = useState("watch"); // watch | quiz | result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const { user } = useAuth();

  useEffect(() => { if (user?.role === "freelancer") api.post(`/courses/${course.id}/start`).catch(() => {}); }, [course.id, user]);

  const submitQuiz = async () => {
    const arr = course.quiz.map((_, i) => answers[i] ?? -1);
    if (arr.some(a => a < 0)) { toast.error("Answer all questions"); return; }
    try {
      const { data } = await api.post("/courses/quiz", { course_id: course.id, answers: arr });
      setResult(data); setStage("result");
      if (data.completed) toast.success("Congrats — you passed!");
    } catch { toast.error("Submission failed"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="font-heading">{course.title}</DialogTitle></DialogHeader>
        {stage === "watch" && (
          <div className="space-y-4">
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <iframe src={`https://www.youtube.com/embed/${course.youtube_id}`} title={course.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
            </div>
            <p className="text-sm text-slate-600">{course.description}</p>
            {user?.role === "freelancer" ? (
              <Button onClick={() => setStage("quiz")} className="btn-orange hover:bg-[#FF6A00] w-full" data-testid="take-quiz">Take the quiz</Button>
            ) : (
              <p className="text-xs text-slate-500 text-center">Log in as a freelancer to take the quiz and earn certificates.</p>
            )}
          </div>
        )}
        {stage === "quiz" && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {course.quiz.map((q, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-4">
                <div className="font-semibold mb-3">{i + 1}. {q.q}</div>
                <div className="space-y-2">
                  {q.options.map((o, oi) => (
                    <label key={oi} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border ${answers[i] === oi ? "border-[#155EEF] bg-[#155EEF]/5" : "border-slate-200"}`} data-testid={`quiz-${i}-${oi}`}>
                      <input type="radio" name={`q${i}`} checked={answers[i] === oi} onChange={() => setAnswers({ ...answers, [i]: oi })} className="accent-[#155EEF]" />
                      <span className="text-sm">{o}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <Button onClick={submitQuiz} className="btn-orange hover:bg-[#FF6A00] w-full" data-testid="submit-quiz">Submit</Button>
          </div>
        )}
        {stage === "result" && result && (
          <div className="text-center py-6">
            {result.completed ? <Award className="mx-auto text-[#FF6A00]" size={64} /> : <div className="mx-auto text-slate-400 text-6xl">😕</div>}
            <div className="mt-4 font-heading font-bold text-3xl">{result.completed ? "Certificate earned!" : "Not quite yet"}</div>
            <div className="mt-2 text-slate-600">Score: <b>{result.score}%</b></div>
            {!result.completed && <p className="mt-3 text-sm text-slate-500">You need 60% to earn a certificate. Try again anytime.</p>}
            <Button onClick={onClose} className="mt-6 btn-orange hover:bg-[#FF6A00]">Close</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
